import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, Lock } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token, new_password: pw, confirm_password: confirm,
      });
      toast.success(data.message);
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-white px-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-3">
          <img src="/tfsi_mark.png" alt="TFSI logo" className="h-10 w-10" />
          <p className="font-heading text-xl font-extrabold tracking-tight text-[#032852]">TFS CRM</p>
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Password recovery</p>
        <h2 className="mt-2 font-heading text-3xl font-black tracking-tight text-[#09090B]">Set a new password</h2>
        <p className="mt-2 text-sm text-[#6D6D70]">Choose a new password for your TFS CRM account (at least 8 characters).</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
          <div className="space-y-2">
            <Label htmlFor="pw" className="text-xs font-bold uppercase tracking-wide text-[#52525B]">New password</Label>
            <Input id="pw" type="password" required minLength={8} autoComplete="new-password" value={pw}
              onChange={(e) => setPw(e.target.value)} className="rounded-sm" data-testid="reset-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Confirm new password</Label>
            <Input id="confirm" type="password" required minLength={8} autoComplete="new-password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)} className="rounded-sm" data-testid="reset-confirm-input" />
          </div>
          {error && (
            <p className="rounded-sm border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]" data-testid="reset-error">{error}</p>
          )}
          <Button type="submit" disabled={loading || !token}
            className="w-full rounded-sm bg-[#569A1F] py-5 font-semibold text-white hover:bg-[#457d18]"
            data-testid="reset-submit-btn">
            {loading ? <CircleNotch size={18} className="animate-spin" /> : (<><Lock size={16} /> Update password</>)}
          </Button>
          {!token && (
            <p className="text-sm text-[#EF4444]" data-testid="reset-missing-token">This reset link is incomplete — request a new one.</p>
          )}
        </form>
        <p className="mt-8 text-center text-xs text-[#6D6D70]">
          <Link to="/login" className="font-bold text-[#032852] hover:underline">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
